import { Component, OnInit, Input, ViewChild, ElementRef, AfterViewChecked } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from 'src/app/api/global.service';
import { QuickCommentComponent } from './quick-add/quick-add.component';

@Component({
  selector: 'app-comments-table',
  templateUrl: './comments.component.html',
  styleUrls: ['./comments.component.scss'],
})
export class CommentsComponent implements OnInit, AfterViewChecked {
  @ViewChild('createCommentRef', { static: false }) createCommentRef: ElementRef | undefined;

  @Input() comments: any = null;
  @Input() url: any = '';
  @Input() guid: any = '';
  @Input() max_height: number = 350;
  // When true, a comment left here is visible to the client (e.g. secure email threads) - the comment window shows the
  // toggle for it. Off by default since most comment contexts are internal-only.
  @Input() visible_to_client = false;
  // Opened from an @mention notification: { user_guid, text } - the comment that mentions this person (and starts with this text) is
  // scrolled to and highlighted once the comments are shown
  @Input() focus_comment: { user_guid: string; text: string } | null = null;
  highlight_guid = '';
  private focus_done = false;
  // The host page shows its own Add Comment button (it calls AddComment())
  @Input() hide_add_button = false;
  current_user: any = null;

  // The saved draft of a new comment (one per url+guid), handed to the comment window when it opens
  draft_comment = '';
  draft_visible_to_client = false;

  // Admin-only toggle - the backend only honors show_deleted=1 for an admin (see Comments.GetComments),
  // so this is always false/hidden for anyone else regardless of local state.
  show_deleted = false;
  comments_loading = false;

  constructor(public globalService: GlobalService, private apiService: ApiService, private modalController: ModalController, private host: ElementRef, private alertController: AlertController) { }

  ngAfterViewChecked() {
    if (!this.focus_comment || this.focus_done || !this.comments?.length) return;
    this.focus_done = true;
    setTimeout(() => this.FocusComment());
  }

  // The newest comment that @mentions the person (the list is newest first). When the notification's text is known, a comment
  // that also starts with it is preferred, in case there are several.
  private FocusComment() {
    const { user_guid, text } = this.focus_comment || { user_guid: '', text: '' };
    const plain = (html: string) => (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
    const mentions = (this.comments || []).filter((c: any) => (c.comment || '').includes(`data-id="${user_guid}"`));
    const snippet = plain(text).slice(0, 30);
    const match = (snippet && mentions.find((c: any) => plain(c.comment).includes(snippet))) || mentions[0];
    if (!match) return;
    this.highlight_guid = match.guid;
    setTimeout(() => {
      const el = (this.host.nativeElement as HTMLElement).querySelector(`[data-comment-guid="${match.guid}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    setTimeout(() => { this.highlight_guid = ''; }, 5000);
  }

  async ngOnInit() {
    this.current_user = this.globalService.GetCurrentUser();
    this.draft_visible_to_client = this.visible_to_client;
    this.LoadDraft();
  }

  // ── Comment draft (one per url+guid, e.g. "companies/comments" + this company's guid) ─────────
  // Unlike secure-email drafts, this is a single autosave-style slot per item rather than a named,
  // browsable list - saving overwrites whatever was there, and it's only ever loaded back when this
  // exact same comment box (same type AND same item) is opened again.

  async LoadDraft() {
    if (!this.url || !this.guid) return;
    const data: any = await this.apiService.get(`comment_drafts?type=${encodeURIComponent(this.url)}&guid=${this.guid}`);
    if (!data.success || !data.data || !data.data[0]) return;
    try {
      const saved = JSON.parse(data.data[0].data);
      this.draft_comment = saved?.comment || '';
      if (this.visible_to_client && typeof saved?.visible_to_client === 'boolean') this.draft_visible_to_client = saved.visible_to_client;
    } catch (e) {
      console.error('Failed to load comment draft', e);
    }
  }

  private async DeleteDraft() {
    this.draft_comment = '';
    if (!this.url || !this.guid) return;
    await this.apiService.delete(`comment_drafts?type=${encodeURIComponent(this.url)}&guid=${this.guid}`, null);
  }

  // Re-fetches straight from this comment box's own GET endpoint (the same {url}/{guid} used for
  // create/update/delete) rather than relying on the parent-supplied @Input comments - the parent's
  // own list is never deleted-inclusive, and toggling this is meant to only affect this comment box.
  async LoadComments() {
    if (!this.url || !this.guid) return;
    this.comments_loading = true;
    const query = this.show_deleted ? '?show_deleted=1' : '';
    const data: any = await this.apiService.get(`${this.url}/${this.guid}${query}`);
    this.comments = data.success ? (data.data || []) : [];
    this.comments_loading = false;
  }

  async ToggleShowDeleted() {
    this.show_deleted = !this.show_deleted;
    await this.LoadComments();
  }

  // ── Writing a comment happens in its own window (new comment, reply, or edit) ─────────────────

  private async OpenCompose(options: { reply?: any; edit?: any } = {}) {
    const { reply, edit } = options;
    const is_new = !reply && !edit;
    const modal = await this.modalController.create({
      component: QuickCommentComponent,
      cssClass: 'small-modal tall-modal secondary-modal',
      componentProps: {
        url: this.url,
        guid: this.guid,
        title: reply ? `Reply to ${reply.users?.[0]?.display_name || 'comment'}` : (edit ? 'Edit Comment' : 'Add Comment'),
        visible_to_client: this.visible_to_client,
        reply_to: reply ? { guid: reply.guid, name: reply.users?.[0]?.display_name, text: reply.comment, date: reply.created_date } : null,
        edit_comment: edit ? JSON.parse(JSON.stringify(edit)) : null,
        initial_comment: is_new ? this.draft_comment : '',
        initial_visible_to_client: is_new ? this.draft_visible_to_client : undefined,
        allow_draft: is_new
      },
      backdropDismiss: false
    });
    modal.onDidDismiss().then((result: any) => {
      const saved = result?.data;
      if (saved?.draft_saved) {
        this.draft_comment = saved.comment || '';
        return;
      }
      if (!saved?.success) return;
      if (edit) {
        // The comment keeps its place in the list, with the new text
        const existing = (this.comments || []).find((c: any) => c.guid === edit.guid);
        if (existing) existing.comment = saved.comment;
        return;
      }
      const created = saved.data?.[0];
      if (created) {
        created.users = [{ display_name: this.current_user.display_name, guid: this.current_user.guid }];
        created.comment = saved.comment;
        if (!this.comments) this.comments = [];
        this.comments.unshift(created);
      }
      if (is_new) this.DeleteDraft();
    });
    return await modal.present();
  }

  AddComment() {
    return this.OpenCompose();
  }

  StartReplyTo(comment) {
    return this.OpenCompose({ reply: comment });
  }

  StartCommentUpdate(comment) {
    return this.OpenCompose({ edit: comment });
  }

  // Asks first - a comment can't be brought back once it is deleted
  async DeleteComment(comment) {
    const alert = await this.alertController.create({
      header: 'Delete Comment',
      message: 'Are you sure you want to delete this comment?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        { text: 'Delete', role: 'destructive', handler: () => this.PerformDeleteComment(comment) }
      ]
    });
    await alert.present();
  }

  private async PerformDeleteComment(comment) {
    let data: any = await this.apiService.delete(`${this.url}/${comment.guid}`,null);
    if (data.success) {
      if (this.show_deleted) {
        // Admin is viewing deleted comments - keep it in place, just flip it to the deleted state,
        // instead of removing it from a list they specifically asked to see deleted items in.
        comment.deleted = true;
        comment.deleted_date = new Date().toISOString();
      } else {
        var index = this.comments.findIndex(item => item.guid === comment.guid);
        this.comments.splice(index, 1);
      }
    }
    this.globalService.presentToastMessage(data.message, data.success);
  }

  CreateCommentHeight() {
    if (this.hide_add_button) return 0;
    if (!this.createCommentRef) return 40;
    const el = this.createCommentRef.nativeElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    return Math.round(rect.height);
  }

}
